import React, { useState } from 'react';
import './PayoutModal.css';

const FULFILLMENT_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' }
];

const CARRIERS = ['USPS', 'UPS', 'FedEx', 'DHL', 'Other'];

const BlanksFulfillModal = ({ order, onClose, onSubmit }) => {
  const initialCarrier = order.carrier && !CARRIERS.includes(order.carrier) ? 'Other' : (order.carrier || '');
  const [status, setStatus] = useState(order.fulfillment_status || 'new');
  const [carrier, setCarrier] = useState(initialCarrier);
  const [carrierOther, setCarrierOther] = useState(
    order.carrier && !CARRIERS.includes(order.carrier) ? order.carrier : ''
  );
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '');
  const [notes, setNotes] = useState(order.admin_notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
  const addr = order.shipping_address || {};

  const resolvedCarrier = carrier === 'Other' ? carrierOther.trim() : carrier;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'shipped') {
      if (!trackingNumber.trim()) {
        setError('Tracking number is required to mark as shipped');
        return;
      }
      if (!resolvedCarrier) {
        setError('Select a carrier to mark as shipped');
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(order.order_id, {
        fulfillmentStatus: status,
        trackingNumber: trackingNumber.trim() || undefined,
        carrier: resolvedCarrier || undefined,
        adminNotes: notes.trim() || undefined
      });
    } catch (err) {
      setError(err.message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Fulfill Order</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Order Summary */}
        <div className="modal-info">
          <div className="modal-info-row">
            <span>Customer</span>
            <span className="modal-info-value">{order.customer_name || '—'}</span>
          </div>
          <div className="modal-info-row">
            <span>Email</span>
            <span className="modal-info-value">{order.client_email || '—'}</span>
          </div>
          <div className="modal-info-row">
            <span>Amount</span>
            <span className="modal-info-value modal-amount">
              ${Number(order.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="modal-info-row">
            <span>Order ID</span>
            <span className="modal-info-value modal-ref">{order.order_id}</span>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="modal-payout-info">
          <h4>Ship To</h4>
          <div className="modal-payout-details">
            {addr.line1 && (
              <div className="modal-detail-row"><span>Address</span><span>{addr.line1}</span></div>
            )}
            <div className="modal-detail-row">
              <span>City / State</span>
              <span>{[addr.city, addr.state].filter(Boolean).join(', ') || '—'}</span>
            </div>
            <div className="modal-detail-row">
              <span>Postal / Country</span>
              <span>{[addr.postal_code, addr.country].filter(Boolean).join(', ') || '—'}</span>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="modal-payout-info">
          <h4>Items {lineItems.length > 0 ? `(${lineItems.length})` : ''}</h4>
          {lineItems.length === 0 ? (
            <p className="blank-no-items">No line items recorded for this order.</p>
          ) : (
            <div className="modal-payout-details">
              {lineItems.map((item, i) => (
                <div key={i} className="modal-detail-row">
                  <span>
                    {item.product}
                    {item.color ? ` · ${item.color}` : ''}
                    {item.size ? ` · ${item.size}` : ''}
                  </span>
                  <span>×{item.quantity}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="modal-field">
            <label>Fulfillment Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {FULFILLMENT_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="modal-field">
            <label>Carrier {status === 'shipped' ? '*' : ''}</label>
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              <option value="">Select carrier</option>
              {CARRIERS.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {carrier === 'Other' && (
            <div className="modal-field">
              <label>Carrier Name</label>
              <input
                type="text"
                placeholder="e.g. Canada Post"
                value={carrierOther}
                onChange={(e) => setCarrierOther(e.target.value)}
              />
            </div>
          )}

          <div className="modal-field">
            <label>Tracking Number {status === 'shipped' ? '*' : ''}</label>
            <input
              type="text"
              placeholder="e.g. 1Z999AA10123456784"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
            />
          </div>

          <div className="modal-field">
            <label>Notes (optional)</label>
            <textarea
              placeholder="Any internal notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {status === 'shipped' && order.client_email && (
            <p className="blank-email-hint">
              The customer will be emailed a shipping confirmation{trackingNumber.trim() ? ' with tracking' : ''}.
            </p>
          )}

          {error && <div className="modal-error">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlanksFulfillModal;
